import { ui } from './host';
import type { MemberStage } from './stages';

/**
 * Member-shaped types and the helpers that render them.
 *
 * Lifted OUT of the web app rather than copied: `admin.ts` is the admin panel's API
 * CLIENT, and none of this is its API or its client — it is what a member looks like
 * once fetched, and how a date reads. The extension held a hand-maintained second
 * copy whose own header said "keep the type shapes and time logic in sync with the
 * web app until a shared package lands". This is that package; the copy is gone.
 *
 * `admin.ts` and `revenue.ts` re-export everything here, so the existing call sites
 * across the web app keep working and there is still exactly one definition.
 */

/**
 * Parse a server timestamp safely. The API stores UTC but often serialises it
 * WITHOUT a timezone suffix (naive `datetime.isoformat()`), and `new Date()` reads
 * a tz-less date-time as LOCAL time — so a UTC instant shows up shifted by the
 * viewer's offset (e.g. "2 hours ago" in Berlin for something that just happened).
 * Pin a tz-less time-bearing string to UTC; pass through anything already zoned
 * ('Z' / ±hh:mm) or date-only, so this stays correct if the backend later emits a
 * proper offset. Use this everywhere instead of `new Date(iso)` / `Date.parse(iso)`.
 */
export function parseTs(iso: string): Date {
  const s = iso.trim();
  const zoned = /[zZ]$|[+-]\d{2}:?\d{2}$/.test(s);
  return new Date(s.includes('T') && !zoned ? s + 'Z' : s);
}

/**
 * Human-readable "time since" for a timestamp, e.g. "just now", "15 minutes ago",
 * "2 hours ago", "3 days ago". Pass `nowMs` (a reactive $state) to keep it live.
 * Returns "—" for a null/invalid input.
 */
export function timeAgo(iso: string | null, nowMs: number = Date.now()): string {
  if (!iso) return '—';
  const t = parseTs(iso).getTime();
  if (Number.isNaN(t)) return '—';
  const s = Math.max(0, (nowMs - t) / 1000);
  if (s < 45) return 'just now';
  const unit = (n: number, name: string) => `${n} ${name}${n === 1 ? '' : 's'} ago`;
  const m = Math.floor(s / 60);
  if (m < 60) return unit(Math.max(1, m), 'minute');
  const h = Math.floor(m / 60);
  if (h < 24) return unit(h, 'hour');
  const d = Math.floor(h / 24);
  if (d < 30) return unit(d, 'day');
  const mo = Math.floor(d / 30);
  if (mo < 12) return unit(mo, 'month');
  return unit(Math.floor(mo / 12), 'year');
}

/**
 * `19d`, `4h`, `now` — the compact form Skool itself uses beside a comment author.
 *
 * A thread is a list of the same fact repeated once per comment, and "19 hours ago"
 * spends eleven characters saying what "19h" says in three. The long form stays
 * wherever the timestamp is the point rather than a footnote.
 */
export function timeAgoShort(iso: string | null, nowMs: number = Date.now()): string {
  if (!iso) return '';
  const t = parseTs(iso).getTime();
  if (Number.isNaN(t)) return '';
  const s = Math.max(0, (nowMs - t) / 1000);
  if (s < 60) return 'now';
  const m = Math.floor(s / 60);
  if (m < 60) return `${m}m`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h`;
  const d = Math.floor(h / 24);
  if (d < 7) return `${d}d`;
  const w = Math.floor(d / 7);
  if (d < 30) return `${w}w`;
  const mo = Math.floor(d / 30);
  if (mo < 12) return `${mo}mo`;
  return `${Math.floor(mo / 12)}y`;
}

/**
 * The forward-looking twin of `timeAgo`: "today", "tomorrow", "in 6 days",
 * "in 2 months". A date that has already passed reads "overdue" rather than a
 * negative countdown — a renewal in the past means the roster sweep hasn't
 * caught up yet, which is worth showing as-is instead of hiding. Returns "—"
 * for a null/invalid input.
 */
export function timeUntil(iso: string | null, nowMs: number = Date.now()): string {
  if (!iso) return '—';
  const t = parseTs(iso).getTime();
  if (Number.isNaN(t)) return '—';
  const days = Math.ceil((t - nowMs) / 86400000);
  if (days < 0) return 'overdue';
  if (days === 0) return 'today';
  if (days === 1) return 'tomorrow';
  if (days < 30) return `in ${days} days`;
  const mo = Math.round(days / 30.44);
  if (mo < 12) return `in ${mo} month${mo === 1 ? '' : 's'}`;
  const y = Math.round(days / 365);
  return `in ${y} year${y === 1 ? '' : 's'}`;
}

/**
 * Integer cents + an ISO 4217 code (blank/null ⇒ USD) → a display price:
 * 9900 → "$99", 300000 → "$3,000", 8950 → "$89.50", (9900, 'eur') → "€99".
 * Whole amounts drop the ".00" because these read as prices on a pricing page,
 * not as ledger lines — the affiliate ledger keeps its two decimals via
 * `fmtUsd` (affiliates.ts), which stays the helper for money owed. An
 * unrecognised currency code degrades to "99 XYZ" rather than throwing.
 *
 * `currencyDisplay: 'narrowSymbol'` is what makes the line above true. The default
 * ('symbol') disambiguates a currency against the RUNTIME LOCALE, so USD renders as
 * "US$99" for anyone whose browser is not en-US — which is most of them, and which is
 * why prices read "US$97/mo" here despite this comment always having claimed "$99".
 * narrowSymbol asks for the shortest unambiguous form instead: "$99", "€99", "£99".
 * It is a newer option than the rest of Intl, so an engine without it throws RangeError
 * and we step down to the wide symbol rather than losing the price entirely.
 */
export function fmtPrice(cents: number, currency: string | null = null): string {
  const code = (currency || 'usd').toUpperCase();
  const v = cents / 100;
  const digits = Number.isInteger(v) ? 0 : 2;
  const opts = { minimumFractionDigits: digits, maximumFractionDigits: digits };
  const base = { style: 'currency', currency: code, ...opts } as const;
  try {
    return new Intl.NumberFormat(undefined, { ...base, currencyDisplay: 'narrowSymbol' }).format(v);
  } catch {
    try {
      return new Intl.NumberFormat(undefined, base).format(v);
    } catch {
      return `${v.toLocaleString(undefined, opts)} ${code}`;
    }
  }
}

/** Suffix per billing period, for a plan price. A one-off payment gets none: it
 *  isn't a rate, and "$300/one_time" reads as nonsense. */
const PLAN_INTERVAL_SUFFIX: Record<string, string> = { month: '/mo', year: '/yr' };

/** A membership's recurring price, split where a table wants to break it
 *  typographically: the amount (the thing you compare down a column) and its
 *  period (the thing you read once). `PlanCell` sets the period smaller so a
 *  column of prices scans as a ladder of numbers. `period` is '' for a one-off or
 *  an interval-less amount. Null when there is nothing to charge — a free member
 *  or a comped tier, both of which every surface renders as words, not as "$0". */
export function planParts(
  cents: number | null | undefined,
  currency: string | null = null,
  interval: string | null = null,
): { amount: string; period: string } | null {
  if (!cents || cents <= 0) return null;
  return { amount: fmtPrice(cents, currency), period: PLAN_INTERVAL_SUFFIX[interval ?? ''] ?? '' };
}

/** How a paid membership recurs, straight from Skool's `recurring_interval`.
 *  `one_time` is a single payment — it counts towards the plan mix, never towards
 *  MRR. */
export type PlanInterval = 'month' | 'year' | 'one_time';

/** The four plan columns plus the membership status, which is what every surface
 *  needs to render a plan honestly: `status === null` means we have no roster
 *  access to this community, so "Free" would be a guess rather than a fact.
 *  Structural on purpose — `MembershipBlock` (`@api/v1/members`) and
 *  `CancellingMember` both satisfy it without converting. */
export interface MemberPlan {
  plan_currency: string | null;
  plan_amount_cents: number | null;
  plan_interval: PlanInterval | null;
  plan_tier: string | null;
  status: MemberStatus | null;
}

/** What `RenewalCell` needs: the period end, the status that says what that date
 *  MEANS (a next charge, or the day a cancelling membership lapses), and — for the
 *  members table, which lists ex-members — the date it actually ended. Structural,
 *  like `MemberPlan`. */
export interface MemberRenewal {
  status: MemberStatus | null;
  ends_at: string | null;
  departed_at?: string | null;
}

/**
 * Where a member stands in the community, straight off Skool's own members-page
 * tabs (so the filter chips read the way a creator already thinks). `cancelling`
 * is still a member — they lapse on `ends_at`; the last four are all "gone",
 * which the `departed` pseudo-filter selects in one chip. A NULL status means
 * *unknown* (no roster access in that community), never "active".
 */
export type MemberStatus = 'active' | 'cancelling' | 'churned' | 'left' | 'removed' | 'banned';

export interface MemberStatusMeta {
  label: string;
  /** Verb for the membership clock: what the date in the Renewal column *is*. */
  verb: string;
  color: string;
  /** True once the membership has actually ended (Skool's churned tab, split). */
  departed: boolean;
}

// Colours come from the palette the panel already uses (theme semantics for the
// live states, the quadrant slate/ochre for the quiet ones) so a status dot sits
// next to a role dot without introducing a new hue.
export const MEMBER_STATUS_META: Record<MemberStatus, MemberStatusMeta> = {
  active: { label: 'Active', verb: 'renews', color: '#2f8f4e', departed: false },
  cancelling: { label: 'Cancelling', verb: 'churns', color: '#b0751a', departed: false },
  // THE FOUR DEPARTED STATUSES SHARE ONE HUE — the theme's `--color-error` (#c23b32),
  // ramped by severity rather than given four unrelated colours. They previously read as
  // red / grey / amber / near-black, so "gone" looked like four different kinds of fact,
  // and `removed`'s amber collided with `cancelling`'s. One hue says "no longer a
  // member" at a glance; the lightness says how they went.
  churned: { label: 'Churned', verb: 'churned', color: '#c23b32', departed: true },
  left: { label: 'Left', verb: 'left', color: '#c85f56', departed: true },
  removed: { label: 'Removed', verb: 'removed', color: '#a32d25', departed: true },
  banned: { label: 'Banned', verb: 'banned', color: '#7a1d17', departed: true },
};

/** Meta for a status string, or null when it's unknown/absent (a value Skool
 *  adds later lands as data, so callers render nothing rather than guessing). */
export function memberStatusMeta(status: string | null | undefined): MemberStatusMeta | null {
  return status ? (MEMBER_STATUS_META[status as MemberStatus] ?? null) : null;
}

export interface HeatmapDay {
  date: string;
  posts: number;
  comments: number;
  total: number;
  /** Upvotes sitting on the posts/comments written that day. Counted on the day the
   *  content was WRITTEN — an upvote has no timestamp of its own — so this reads as
   *  "how well that day's contributions landed". Absent on older payloads. */
  likes?: number;
  // Present on the community activity heatmap: that day's contributions split by
  // contributor role (role → count). Absent on single-member heatmaps.
  roles?: Record<string, number>;
}

// One merged segment of a member's contributor-role journey (oldest → newest).
export interface RoleJourneySegment {
  quadrant: string;
  days: number;
  start: string;
  end: string;
  activity: number;
}

/** Elapsed time, table-width: "today", "6d ago", "3mo ago", "2y ago" (or "—"). */
export function agoShort(iso: string | null | undefined): string {
  if (!iso) return '—';
  const t = parseTs(iso).getTime();
  if (Number.isNaN(t)) return '—';
  const s = Math.max(0, (Date.now() - t) / 1000);
  if (s < 86400) return 'today';
  if (s < 2592000) return `${Math.floor(s / 86400)}d ago`;
  if (s < 31536000) return `${Math.floor(s / 2592000)}mo ago`;
  return `${Math.floor(s / 31536000)}y ago`;
}

/** Short calendar date, with the year only when it isn't this one: "12 Aug". */
export function calendarShort(iso: string): string {
  const d = parseTs(iso);
  const opts: Intl.DateTimeFormatOptions = { month: 'short', day: 'numeric' };
  if (d.getFullYear() !== new Date().getFullYear()) opts.year = 'numeric';
  return d.toLocaleDateString(undefined, opts);
}

/** The unabbreviated date, for a cell's hover title. */
export function fullDate(iso: string): string {
  return parseTs(iso).toLocaleDateString(undefined, { month: 'long', day: 'numeric', year: 'numeric' });
}

/** One step of the role journey, as `/v1` puts it on the wire. */
export interface JourneyStep {
  quadrant: string;
  days: number;
  start: string | null;
  end: string | null;
  activity: number | null;
}

/**
 * `<RoleJourney>` needs `start`/`end`/`activity` present; on the wire they are
 * nullable because a segment can be open at either end. Filled once, here, rather
 * than at each render site.
 */
export function journeySegments(steps: JourneyStep[] | undefined): RoleJourneySegment[] {
  return (steps ?? []).map((s) => ({
    quadrant: s.quadrant,
    days: s.days,
    start: s.start ?? '',
    end: s.end ?? '',
    activity: s.activity ?? 0,
  }));
}

/** The contribution block — default-on for `…/members`. `first_active`/`last_active`
 *  are INSTANTS here (§1.5), where `/admin/analytics/member` emits a bare
 *  `YYYY-MM-DD` for the same key; parse with `parseTs`, never `new Date`. */
export interface StatsBlock {
  posts: number | null;
  comments: number | null;
  activity: number | null;
  upvotes_received: number | null;
  replies_earned: number | null;
  momentum: number | null;
  stage: MemberStage | null;
  quadrant: string | null;
  style: string | null;
  first_active: string | null;
  last_active: string | null;
}

/**
 * One member, as the HOVERCARD reads them.
 *
 * A deliberately NARROW view of what `GET /v1/communities/{id}/members/{member_id}`
 * returns — the fields this card renders and no others. Structural typing means it
 * is the same object either app already receives, not a conversion of it, so there
 * is no mapping step and no second definition of a member to keep in step.
 *
 * `membership: null` is the honest empty and not a placeholder: the block is nulled
 * when the caller holds no staff role in the community, which is why `<PlanCell>`
 * renders "—" ("we cannot see it") instead of "Free" ("they pay nothing").
 */
/**
 * A member's own page on Skool, or null when we hold no handle to build one from.
 *
 * One definition because it was being rebuilt inline wherever it was wanted — the DM
 * transcript did `https://www.skool.com/@${handle}` by hand — and a profile URL that
 * differs by surface is a link that works on one screen and 404s on the next.
 */
export function skoolProfileUrl(handle: string | null | undefined): string | null {
  return handle ? `https://www.skool.com/@${handle}` : null;
}

export interface MemberCard {
  member_id: number;
  name: string;
  picture_profile: string | null;
  joined_at?: string | null;
  journey?: JourneyStep[];
  stats?: StatsBlock | null;
  /** `activity=dense` — 371 contiguous ascending days. `<Heatmap>` derives every
   *  cell's weekday column from the FIRST day, so a gapped series shifts the year. */
  activity?: { days: HeatmapDay[]; max: number } | null;
  membership?: (MemberPlan & MemberRenewal & { status: MemberStatus | null }) | null;
}

/**
 * Fetch one member card, asking for exactly what the card draws.
 *
 * `activity=dense` is deliberate and is the ONE place a surface should ask for it:
 * every list stays on `weekly`.
 *
 * Generic over the response so the two callers can each name what they read: the
 * hovercard wants `MemberCard`, the insights panel wants the fuller `Member`.
 * Same route, same params, one place they are written down — and no wider a claim
 * than `apiGet<T>` already makes, since JSON has no types either way.
 */
export async function fetchMemberCard<M extends MemberCard = MemberCard>(
  skoolId: number,
  memberId: number,
): Promise<M> {
  const { member } = await ui().apiGet<{ member: M }>(
    `/v1/communities/${skoolId}/members/${memberId}`,
    { include: ['journey', 'activity'], activity: 'dense' },
  );
  return member;
}

/**
 * Media a Skool post, comment or message can carry, as `<SkoolMedia>` renders it.
 * Moved here with that component — the shapes exist to be drawn.
 */
/** A stored image (uploaded picture or giphy gif) — a public, hotlinkable URL. */
// The Skool-content and moderation shapes moved to `@api/v1/shapes` — beside the
// clients that produce them. Re-exported here because fourteen components import
// them from this module and none of them cares where a shape is declared.
export type {
  CommentItem,
  CommentReason,
  ImageMedia,
  ModerationItem,
  ModerationStats,
  Moderator,
  PostAuthor,
  PostDetail,
  VideoMedia,
} from '@api/v1/shapes';